import { CONTENT_TEMPLATE_PRESETS, createContentDocument, instantiateContentTemplate, parseContentDocument, serializeContentDocument } from './document.mjs';
import { createIndexedDbTemplateStore, createTemplateLibrary } from './templates.mjs';

const DB_NAME = 'edusense-content-studio';
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 4);
    request.onupgradeneeded = () => {
      for (const name of ['drafts', 'sessions', 'templates', 'assets']) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name, { keyPath: 'id' });
      }
    };
    request.onerror = () => reject(new Error('기기 저장소를 열지 못했습니다. 파일로 내보내 내용을 보관해 주세요.'));
    request.onblocked = () => reject(new Error('다른 탭에서 제작실을 닫은 후 다시 저장해 주세요.'));
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
  });
}

async function listRecords(storeName, message) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    tx.oncomplete = () => { db.close(); resolve(req.result.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))); };
    tx.onerror = () => { db.close(); reject(new Error(message)); };
  });
}

export const listLocalDrafts = () => listRecords('drafts', '저장한 문서 목록을 읽지 못했습니다.');
export const listLocalTemplates = () => contentTemplates.listSaved();
export const listLocalAssets = () => listRecords('assets', '저장한 이미지 에셋 목록을 읽지 못했습니다.');

export async function saveLocalAsset(record) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('assets', 'readwrite');
    tx.objectStore('assets').add(record);
    tx.oncomplete = () => { db.close(); resolve(record); };
    tx.onabort = tx.onerror = () => { db.close(); reject(new Error('이미 등록된 이미지이거나 저장 공간이 부족합니다.')); };
  });
}

// One template library for built-in presets (including the 18 Saju products),
// templates saved in this browser and, when `remote` is the signed-in
// creator's account client, templates saved to the account or shared with
// every creator. Existing device records keep their { id, title, markdown,
// updatedAt } shape, so templates saved before remain.
export function createContentTemplateLibrary({ remote = null } = {}) {
  return createTemplateLibrary({
    families: CONTENT_TEMPLATE_PRESETS,
    create: (templateId, example) => createContentDocument(templateId, { example }),
    codec: { serialize: serializeContentDocument, parse: parseContentDocument, instantiate: instantiateContentTemplate },
    store: createIndexedDbTemplateStore({ openDatabase }),
    remote,
  });
}
export const contentTemplates = createContentTemplateLibrary();

export const saveLocalTemplate = ({ name, document }) => contentTemplates.save({ name, document });
export const deleteLocalTemplate = (id) => contentTemplates.remove(id);

export async function saveLocalDraft({ id, revision = 0, document, sourceDraftId }) {
  const markdown = serializeContentDocument(document);
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('drafts', 'readwrite');
    const store = tx.objectStore('drafts');
    const request = store.get(id);
    let saved;
    let failure;
    request.onsuccess = () => {
      if ((request.result?.revision ?? 0) !== revision) {
        failure = Object.assign(new Error('다른 탭에서 이 문서를 수정했습니다. 현재 작업은 사본 저장으로 보관한 뒤 최신 문서를 열어 비교해 주세요.'), {
          code: 'LOCAL_DRAFT_CONFLICT',
        });
        tx.abort();
        return;
      }
      const originalDraftId = request.result?.sourceDraftId ?? sourceDraftId;
      saved = { id, revision: revision + 1, title: document.title || '제목 없는 문서', markdown, updatedAt: new Date().toISOString(),
        ...(originalDraftId ? { sourceDraftId: originalDraftId } : {}) };
      store.put(saved);
    };
    tx.oncomplete = () => { db.close(); resolve(saved); };
    tx.onabort = () => { db.close(); reject(failure ?? new Error('저장 공간 또는 브라우저 권한을 확인해 주세요.')); };
    tx.onerror = () => { failure = new Error('문서를 저장하지 못했습니다. 파일로 내보내 보관해 주세요.'); };
  });
}

export function readLocalDraft(record) {
  const document = parseContentDocument(record.markdown);
  if (!document) throw new Error('저장한 문서 형식을 인식하지 못했습니다.');
  return document;
}

// Working copies never update the explicitly saved draft or its revision. Each
// mounted workspace owns a new key, including tabs duplicated by the browser.
export async function readWorkspaceSession(sessionId) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('sessions', 'readonly');
    const request = tx.objectStore('sessions').getAll();
    tx.oncomplete = () => {
      db.close();
      const records = request.result;
      const record = records.find(item => item.id === sessionId)
        ?? records.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      try { resolve(record ? { ...record, document: readLocalDraft(record) } : null); }
      catch (reason) { reject(reason); }
    };
    tx.onerror = () => { db.close(); reject(new Error('이전 작업을 복원하지 못했습니다. 저장한 콘텐츠에서 다시 열어 주세요.')); };
  });
}

/** The latest working copy of content opened from template `templateId`, from any tab, or null. */
export async function findTemplateSession(templateId) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('sessions', 'readonly');
    const request = tx.objectStore('sessions').getAll();
    tx.oncomplete = () => {
      db.close();
      const record = request.result.filter(item => item.templateId === templateId)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      try { resolve(record ? { ...record, document: readLocalDraft(record) } : null); }
      catch (reason) { reject(reason); }
    };
    tx.onerror = () => { db.close(); reject(new Error('이 템플릿의 이전 작업을 찾지 못했습니다.')); };
  });
}

export async function saveWorkspaceSession({ sessionId, draftId, revision, document, activeSectionId, editingView, dirty, templateId = null }) {
  const record = { id: sessionId, draftId, revision, markdown: serializeContentDocument(document), activeSectionId,
    editingView, dirty, templateId, updatedAt: new Date().toISOString() };
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('sessions', 'readwrite');
    const store = tx.objectStore('sessions');
    store.put(record);
    // A duplicated tab inherits the previous key in sessionStorage. Keep that
    // snapshot: the original tab may still need it when it reloads.
    tx.oncomplete = () => { db.close(); resolve(record); };
    tx.onabort = tx.onerror = () => { db.close(); reject(new Error('작업을 자동 보관하지 못했습니다. 저장 공간을 확인하거나 원본 파일로 내보내 주세요.')); };
  });
}
